// src/client/components/ErrorBoundary.tsx
// NOTE: React ErrorBoundary catches errors thrown during render, lifecycle, and
// constructors only. Errors in event handlers, async code, or setState callbacks
// are NOT caught and must be handled at the call site.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Sentry } from '@client/lib/sentry';

interface Props { children: ReactNode; }
interface State { error: Error | null; }

export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack ?? '' } });
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div style={{ padding: 24 }}>
          <h2>Something went wrong</h2>
          <p className="small">Please try again. If the problem persists, reload the app.</p>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            style={{ marginTop: 12, padding: '8px 16px' }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
